import React, { useEffect, useRef, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { MapContainer, TileLayer, Marker, Tooltip, useMap } from 'react-leaflet';
import { CheckCircle, ChevronLeft, ChevronRight, Edit, Save, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import { AreaOverlay } from '../map/AreaOverlay';
import { CompactMapSidebar } from './CompactMapSidebar';
import { Button } from '../ui';
import { buildFarmGrouping, getSectionIdsForGroup } from '../../utils/areaOverlayUtils';
import 'leaflet/dist/leaflet.css';
import '../../styles/leaflet-overrides.css';
import '../map/LeafletMap.css';
import './CompactMapView.css';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const getStationIconSvg = (sensorType) => {
  if (sensorType === 'retro_modulo') {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
        <!-- Station base/pole -->
        <line x1="12" y1="19" x2="12" y2="22" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"></line>
        <!-- Solar panel on top -->
        <rect x="5" y="4" width="14" height="8" rx="1" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></rect>
        <line x1="5" y1="7" x2="19" y2="7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></line>
        <line x1="5" y1="10" x2="19" y2="10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></line>
        <line x1="9" y1="4" x2="9" y2="12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></line>
        <line x1="15" y1="4" x2="15" y2="12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></line>
        <!-- Station housing below panel -->
        <rect x="8" y="12" width="8" height="6" rx="1" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></rect>
        <!-- Sensor indicator -->
        <circle cx="12" cy="15" r="1.5" fill="currentColor" opacity="0.6"></circle>
      </svg>
    `;
  }

  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
      <!-- Station base/pole -->
      <line x1="12" y1="19" x2="12" y2="22" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"></line>
      <!-- Station housing -->
      <rect x="7" y="11" width="10" height="7" rx="1.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></rect>
      <!-- Weather sensor on top -->
      <circle cx="12" cy="8" r="2.5" fill="none" stroke="currentColor" stroke-width="2"></circle>
      <line x1="12" y1="10.5" x2="12" y2="11" stroke="currentColor" stroke-width="2" stroke-linecap="round"></line>
      <!-- Wind vane -->
      <path d="M12 8 L14 6 M12 8 L10 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path>
      <!-- Signal waves -->
      <path d="M8 8 Q12 4 16 8" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" opacity="0.7"></path>
      <!-- Display/screen -->
      <rect x="9" y="13" width="6" height="3" rx="0.5" fill="currentColor" opacity="0.2"></rect>
      <line x1="10" y1="14.5" x2="14" y2="14.5" stroke="currentColor" stroke-width="1" stroke-linecap="round"></line>
    </svg>
  `;
};

// Create custom marker icon for stations (from LeafletMap)
const createCustomIcon = (stationCode, status, sensorType) => {
  const colors = {
    active: '#5A7A3A',
    warning: '#8A7A4A',
    inactive: '#8A4A3A'
  };

  const bgColor = colors[status] || colors.active;
  const pulse = status === 'active' ? 'marker-pulse' : status === 'warning' ? 'marker-pulse-fast' : '';
  const iconSvg = getStationIconSvg(sensorType);
  const colorStyle = `border-color: ${bgColor}; color: ${bgColor};`;

  return L.divIcon({
    html: `
      <div class="custom-marker-wrapper ${pulse}">
        <div class="custom-marker-icon" style="${colorStyle}">
          ${iconSvg}
        </div>
      </div>
    `,
    className: 'custom-marker-container',
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16]
  });
};

// Component to fit map bounds to polygons, then lock zoom at maximum level
function FitBounds({ stations, groups, farm }) {
  const map = useMap();

  useEffect(() => {
    const bounds = L.latLngBounds();
    let hasPoints = false;

    const toNumber = (value) => {
      if (value == null) return null;
      if (typeof value === 'number') return value;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    };

    const extendPoint = (lat, lon) => {
      const latNum = toNumber(lat);
      const lonNum = toNumber(lon);
      if (!Number.isFinite(latNum) || !Number.isFinite(lonNum)) return;
      bounds.extend([latNum, lonNum]);
      hasPoints = true;
    };

    const extendPolygon = (polygon) => {
      if (!Array.isArray(polygon)) return;
      polygon.forEach((point) => {
        if (!Array.isArray(point) || point.length < 2) return;
        extendPoint(point[0], point[1]);
      });
    };

    const extendBounds = (areaBounds) => {
      if (!areaBounds) return;
      extendPoint(areaBounds.minLat, areaBounds.minLon);
      extendPoint(areaBounds.minLat, areaBounds.maxLon);
      extendPoint(areaBounds.maxLat, areaBounds.minLon);
      extendPoint(areaBounds.maxLat, areaBounds.maxLon);
    };

    const hasGroups = Array.isArray(groups) && groups.length > 0;

    if (hasGroups) {
      groups.forEach((group) => {
        if (Array.isArray(group?.polygon) && group.polygon.length > 0) {
          extendPolygon(group.polygon);
          return;
        }
        if (group?.bounds) {
          extendBounds(group.bounds);
          return;
        }
        if (group?.position?.lat != null && group?.position?.lon != null) {
          extendPoint(group.position.lat, group.position.lon);
          return;
        }
        if (Array.isArray(group?.center) && group.center.length === 2) {
          extendPoint(group.center[0], group.center[1]);
        }
      });
    }

    if (!hasPoints && !hasGroups && farm?.polygon && farm.polygon.length > 0) {
      extendPolygon(farm.polygon);
    }

    if (!hasPoints && !hasGroups && farm?.bounds) {
      const { north, south, east, west } = farm.bounds;
      if ([north, south, east, west].every(v => Number.isFinite(v))) {
        extendBounds({ minLat: south, maxLat: north, minLon: west, maxLon: east });
      }
    }

    if (!hasPoints && !hasGroups && stations && stations.length > 0) {
      stations.forEach((station) => {
        extendPoint(station.locationLat, station.locationLon);
      });
    }

    let rafId = null;
    let timeoutId = null;

    if (bounds.isValid()) {
      // Calculate optimal padding based on map container size
      const container = map.getContainer();
      const containerWidth = container.offsetWidth || 800;
      const containerHeight = container.offsetHeight || 400;
      
      // Zero-padding fit to maximize zoom without clipping.
      const paddingX = 0;
      const paddingY = 0;
      
      // Fit bounds with maximum zoom level (no limit)
      const maxZoom = Number.isFinite(map.getMaxZoom()) ? map.getMaxZoom() : 19;
      const fitMap = () => {
        map.invalidateSize();
        const zoom = map.getBoundsZoom(bounds, false, [paddingY, paddingX]);
        map.setView(bounds.getCenter(), Math.min(zoom, maxZoom), { animate: false });
      };

      map.whenReady(() => {
        fitMap();
        rafId = requestAnimationFrame(fitMap);
        timeoutId = setTimeout(fitMap, 120);
      });

      // Lock zoom after fitting (disable zoom controls and interactions, but keep panning)
      setTimeout(() => {
        map.touchZoom.disable();
        map.doubleClickZoom.disable();
        map.scrollWheelZoom.disable();
        map.boxZoom.disable();
        map.keyboard.disable();
        if (map.tap) map.tap.disable();
        // Keep dragging enabled so users can pan the map
      }, 150);
    }

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [map, stations, groups, farm]);

  return null;
}

// Component to ensure markers are interactive
function EnsureMarkerInteractivity() {
  const map = useMap();

  useEffect(() => {
    // Ensure marker pane allows interactions
    const markerPane = map.getPane('markerPane');
    if (markerPane) {
      markerPane.style.pointerEvents = 'none';
    }

    // Find all markers and ensure they're interactive
    map.eachLayer((layer) => {
      if (layer instanceof L.Marker) {
        layer.options.interactive = true;
        layer.options.bubblingMouseEvents = true;
      }
    });
  }, [map]);

  return null;
}

export function CompactMapView({
  sections,
  selectedSections,
  selectAllSections,
  onSectionClick,
  farm,
  stations, // Keep for calculating section positions
  drillState,
  onDrillChange,
  onSectionsChange,
  onSelectAllChange,
  onSave,
  onCancel,
  hasUnsavedChanges = false,
  isEditingSelection = true,
  onEditSelection,
  showSidebar = true,
  interactive = true,
  showLegend = true,
  legendVariant = 'selection',
  legendTitle,
  alwaysShowMarkers = false,
  showMarkerTooltips = false,
  ariaLabel,
}) {
  const { t } = useTranslation();
  const mapRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState(true);
  const isSelectionEditable = interactive && isEditingSelection;
  const isInteractive = interactive && typeof onSectionClick === 'function';

  // Create set of selected section IDs for quick lookup
  const selectedSectionIds = useMemo(() => {
    if (selectAllSections) {
      return new Set(sections.map(s => s.id));
    }
    return new Set(selectedSections);
  }, [selectedSections, selectAllSections, sections]);

  const mapWrapperStyle = useMemo(() => ({
    position: 'relative',
    pointerEvents: interactive || showSidebar ? 'auto' : 'none',
  }), [interactive, showSidebar]);

  // Calculate center and bounds
  const mapCenter = useMemo(() => {
    if (sections.length === 0) {
      return farm?.centerCoordinates || [41.9, 12.5];
    }
    
    const avgLat = sections.reduce((sum, s) => sum + s.position.lat, 0) / sections.length;
    const avgLon = sections.reduce((sum, s) => sum + s.position.lon, 0) / sections.length;
    return [avgLat, avgLon];
  }, [sections, farm]);

  const allSectionIds = useMemo(() => sections.map(section => section.id), [sections]);

  const handleSectionToggle = (sectionId) => {
    if (!isSelectionEditable || !onSectionsChange || !onSelectAllChange) return;
    if (selectAllSections) {
      const remaining = allSectionIds.filter(id => id !== sectionId);
      onSectionsChange(remaining);
      onSelectAllChange(false);
      return;
    }
    const isSelected = selectedSections.includes(sectionId);
    if (isSelected) {
      onSectionsChange(selectedSections.filter(id => id !== sectionId));
    } else {
      onSectionsChange([...selectedSections, sectionId]);
    }
  };

  const handleGroupToggle = (sectionIds) => {
    if (!isSelectionEditable || !onSectionsChange || !onSelectAllChange) return;
    if (!sectionIds || sectionIds.length === 0) return;
    if (selectAllSections) {
      const remaining = allSectionIds.filter(id => !sectionIds.includes(id));
      onSectionsChange(remaining);
      onSelectAllChange(false);
      return;
    }
    const selectedSet = new Set(selectedSections);
    const isFullySelected = sectionIds.every(id => selectedSet.has(id));
    const nextSelection = isFullySelected
      ? selectedSections.filter(id => !sectionIds.includes(id))
      : Array.from(new Set([...selectedSections, ...sectionIds]));
    onSectionsChange(nextSelection);
  };

  const handleSectionClick = (sectionId) => {
    console.log('Section clicked:', sectionId);
    if (!isSelectionEditable) return;
    if (isInteractive) {
      onSectionClick(sectionId);
    } else if (onSectionsChange && onSelectAllChange) {
      handleSectionToggle(sectionId);
    } else {
      console.warn('Selection handlers not provided');
    }
  };

  const groupingStations = useMemo(() => {
    if (farm?.stations && farm.stations.length > 0) {
      return farm.stations;
    }
    return stations;
  }, [farm, stations]);

  const grouping = useMemo(() => {
    return buildFarmGrouping({ farm, sections, stations: groupingStations });
  }, [farm, sections, groupingStations]);

  const showSelectionModeToggle = useMemo(() => {
    return grouping.groupingLevel !== 'section' || grouping.areaHa > 10;
  }, [grouping.groupingLevel, grouping.areaHa]);

  const activeIds = useMemo(() => {
    if (!drillState?.level || !drillState?.id) {
      return { activeQuadrantId: null, activeZoneId: null, activeSectionId: null };
    }
    if (drillState.level === 'quadrant') {
      return { activeQuadrantId: drillState.id, activeZoneId: null, activeSectionId: null };
    }
    if (drillState.level === 'zone') {
      const quadrantId = grouping.zoneIdToQuadrantId?.get(drillState.id) || null;
      return { activeQuadrantId: quadrantId, activeZoneId: drillState.id, activeSectionId: null };
    }
    if (drillState.level === 'section') {
      const zoneId = grouping.sectionIdToZoneId?.get(drillState.id) || null;
      const quadrantId = zoneId ? grouping.zoneIdToQuadrantId?.get(zoneId) || null : null;
      return { activeQuadrantId: quadrantId, activeZoneId: zoneId, activeSectionId: drillState.id };
    }
    return { activeQuadrantId: null, activeZoneId: null, activeSectionId: null };
  }, [drillState, grouping]);

  const { activeQuadrantId, activeZoneId } = activeIds;

  useEffect(() => {
    if (drillState?.level) {
      setSelectionMode(false);
    }
  }, [drillState?.level]);

  useEffect(() => {
    if (!showSelectionModeToggle) {
      setSelectionMode(false);
    }
  }, [showSelectionModeToggle]);

  useEffect(() => {
    if (!isEditingSelection) {
      setSelectionMode(false);
      return;
    }
    if (showSelectionModeToggle) {
      setSelectionMode(true);
    }
  }, [isEditingSelection, showSelectionModeToggle]);


  const activeQuadrant = useMemo(() => {
    return grouping.quadrants.find(q => q.id === activeQuadrantId) || null;
  }, [grouping.quadrants, activeQuadrantId]);

  const activeZone = useMemo(() => {
    return grouping.zones.find(z => z.id === activeZoneId) || null;
  }, [grouping.zones, activeZoneId]);

  const currentLevel = useMemo(() => {
    if (grouping.groupingLevel === 'section') return 'section';
    if (grouping.groupingLevel === 'zone') {
      return activeZoneId ? 'section' : 'zone';
    }
    if (!activeQuadrantId) return 'quadrant';
    if (!activeZoneId) return 'zone';
    return 'section';
  }, [grouping.groupingLevel, activeQuadrantId, activeZoneId]);

  const showMarkers = alwaysShowMarkers || currentLevel === 'section';
  const legendMode = legendVariant || 'selection';
  const showSelectionLegend = legendMode === 'selection' || legendMode === 'both';
  const showStationLegend = legendMode === 'stations' || legendMode === 'both';

  const currentGroups = useMemo(() => {
    if (currentLevel === 'quadrant') return grouping.quadrants;
    if (currentLevel === 'zone') {
      return grouping.groupingLevel === 'quadrant'
        ? (activeQuadrant?.zones || [])
        : grouping.zones;
    }
    if (currentLevel === 'section') {
      if (grouping.groupingLevel === 'quadrant') {
        return activeZone?.sections || [];
      }
      if (grouping.groupingLevel === 'zone') {
        const zone = grouping.zones.find(z => z.id === activeZoneId);
        return zone?.sections || [];
      }
      return grouping.sections;
    }
    return [];
  }, [currentLevel, grouping, activeQuadrant, activeZone, activeZoneId]);

  const viewModeGroups = useMemo(() => {
    const sectionsList = Array.isArray(grouping.sections) ? grouping.sections : [];
    if (!sectionsList.length) return [];
    if (!selectedSectionIds || selectedSectionIds.size === 0) return [];

    const sectionById = new Map(sectionsList.map(section => [section.id, section]));
    const addSectionById = (id, target, seen) => {
      if (!id || seen.has(id)) return;
      const section = sectionById.get(id);
      if (!section) return;
      target.push(section);
      seen.add(id);
    };
    const selectedIn = (ids) => ids.filter(id => selectedSectionIds.has(id));
    const isFullySelected = (ids) => ids.length > 0 && ids.every(id => selectedSectionIds.has(id));

    if (grouping.groupingLevel === 'section') {
      return sectionsList.filter(section => selectedSectionIds.has(section.id));
    }

    if (grouping.groupingLevel === 'zone') {
      const mixed = [];
      const seenSections = new Set();
      grouping.zones.forEach((zone) => {
        const zoneSectionIds = getSectionIdsForGroup(zone);
        const selectedInZone = selectedIn(zoneSectionIds);
        if (selectedInZone.length === 0) return;
        if (isFullySelected(zoneSectionIds)) {
          mixed.push(zone);
          return;
        }
        selectedInZone.forEach((id) => addSectionById(id, mixed, seenSections));
      });
      return mixed;
    }

    const mixed = [];
    const seenSections = new Set();
    grouping.quadrants.forEach((quadrant) => {
      const quadrantSectionIds = getSectionIdsForGroup(quadrant);
      const selectedInQuadrant = selectedIn(quadrantSectionIds);
      if (selectedInQuadrant.length === 0) return;
      if (isFullySelected(quadrantSectionIds)) {
        mixed.push(quadrant);
        return;
      }
      const zones = Array.isArray(quadrant.zones) ? quadrant.zones : [];
      if (zones.length === 0) {
        selectedInQuadrant.forEach((id) => addSectionById(id, mixed, seenSections));
        return;
      }
      zones.forEach((zone) => {
        const zoneSectionIds = getSectionIdsForGroup(zone);
        const selectedInZone = selectedIn(zoneSectionIds);
        if (selectedInZone.length === 0) return;
        if (isFullySelected(zoneSectionIds)) {
          mixed.push(zone);
          return;
        }
        selectedInZone.forEach((id) => addSectionById(id, mixed, seenSections));
      });
    });
    return mixed;
  }, [grouping, selectedSectionIds]);

  const mapGroups = isEditingSelection ? currentGroups : viewModeGroups;
  const mapGroupLevel = isEditingSelection ? currentLevel : 'mixed';

  const breadcrumbItems = useMemo(() => {
    const items = [];
    if (grouping.groupingLevel === 'quadrant' && activeQuadrant) {
      items.push({ id: activeQuadrant.id, name: activeQuadrant.name, level: 'quadrant' });
    }
    if (activeZone) {
      items.push({ id: activeZone.id, name: activeZone.name, level: 'zone' });
    }
    return items;
  }, [grouping.groupingLevel, activeQuadrant, activeZone]);

  const showBreadcrumb = useMemo(() => {
    return (
      breadcrumbItems.length > 0
      || (grouping && grouping.groupingLevel !== 'section' && currentLevel !== grouping.groupingLevel)
    );
  }, [breadcrumbItems, grouping, currentLevel]);

  const drillToQuadrant = (quadrantId) => {
    if (typeof onDrillChange !== 'function') return;
    setSelectionMode(false);
    onDrillChange({ level: 'quadrant', id: quadrantId });
  };

  const drillToZone = (zoneId) => {
    if (typeof onDrillChange !== 'function') return;
    setSelectionMode(false);
    onDrillChange({ level: 'zone', id: zoneId });
  };

  const drillToSection = (sectionId) => {
    if (typeof onDrillChange !== 'function') return;
    setSelectionMode(false);
    onDrillChange({ level: 'section', id: sectionId });
  };

  const handleNavigateUp = () => {
    if (typeof onDrillChange !== 'function') return;
    if (currentLevel === 'section') {
      if (grouping.groupingLevel === 'quadrant' && activeQuadrantId) {
        onDrillChange({ level: 'quadrant', id: activeQuadrantId });
        return;
      }
      if (grouping.groupingLevel === 'zone') {
        onDrillChange(null);
        return;
      }
      onDrillChange(null);
      return;
    }
    if (currentLevel === 'zone' && grouping.groupingLevel === 'quadrant' && activeQuadrantId) {
      onDrillChange(null);
    }
  };

  // Handle polygon/area click - map area name to section ID
  const handleAreaClick = (areaName) => {
    console.log('Area polygon clicked:', areaName);
    if (!interactive) return;
    const clickedGroup = currentGroups.find(group => group.areaName === areaName || group.name === areaName);
    if (!clickedGroup) {
      console.warn('No group found for area name:', areaName);
      return;
    }

    if (selectionMode && isEditingSelection) {
      const sectionIds = Array.isArray(clickedGroup.sectionIds)
        ? clickedGroup.sectionIds.filter(Boolean)
        : (clickedGroup.id ? [clickedGroup.id] : []);
      if (sectionIds.length > 0) {
        handleGroupToggle(sectionIds);
      }
      return;
    }

    if (currentLevel === 'quadrant') {
      drillToQuadrant(clickedGroup.id);
      return;
    }
    if (currentLevel === 'zone') {
      drillToZone(clickedGroup.id);
      return;
    }

    const sectionId = clickedGroup.id;
    if (sectionId) {
      const allowExploreSelection = !showSelectionModeToggle;
      if (!allowExploreSelection) {
        drillToSection(sectionId);
        return;
      }
      handleSectionClick(sectionId);
    }
  };

  if (!sections || sections.length === 0) {
    return (
      <div className="compact-map-view__empty">
        <p>{t('analytics.sections.noneForFarm')}</p>
      </div>
    );
  }

  const viewClassName = [
    'compact-map-view',
    showSidebar && sidebarOpen ? 'compact-map-view--sidebar-open' : '',
    interactive ? '' : 'compact-map-view--static',
  ].filter(Boolean).join(' ');

  return (
    <div className={viewClassName}>
      <div className="compact-map-view__map-wrapper" style={mapWrapperStyle}>
        {showSidebar && (
          <>
            {/* Toggle Button - inside map container */}
            <button
              className={`compact-map-sidebar__toggle ${sidebarOpen ? 'compact-map-sidebar__toggle--open' : ''}`}
              onClick={() => setSidebarOpen(!sidebarOpen)}
              title={sidebarOpen ? t('analytics.map.hideStationList') : t('analytics.map.showStationList')}
            >
              {sidebarOpen ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </button>

            {/* Sidebar - inside map container */}
            <CompactMapSidebar
              sections={sections}
              stations={stations}
              selectedSections={selectedSections}
              selectAllSections={selectAllSections}
              onSectionClick={onSectionClick}
              onSectionsChange={onSectionsChange}
              onSelectAllChange={onSelectAllChange}
              isOpen={sidebarOpen}
              onToggle={() => setSidebarOpen(!sidebarOpen)}
              grouping={grouping}
              currentLevel={currentLevel}
              currentGroups={currentGroups}
              breadcrumbItems={breadcrumbItems}
              onNavigateUp={handleNavigateUp}
              onGroupDrill={(group) => {
                if (currentLevel === 'quadrant') {
                  drillToQuadrant(group.id);
                } else if (currentLevel === 'zone') {
                  drillToZone(group.id);
                }
              }}
              onGroupToggle={handleGroupToggle}
              onSectionToggle={handleSectionToggle}
              selectionDisabled={!isEditingSelection}
            />
          </>
        )}

        {(showBreadcrumb || (isInteractive && showSelectionModeToggle)) && (
          <div className="compact-map-view__controls">
            {isInteractive && showSelectionModeToggle && isEditingSelection && (
              <div
                className="compact-map-view__mode-control"
                role="group"
                aria-label={t('analytics.map.modeLabel', { defaultValue: 'Map mode' })}
              >
                <span className="compact-map-view__mode-label">
                  {t('analytics.map.modeLabel', { defaultValue: 'Map mode' })}
                </span>
                <div className="compact-map-view__mode-toggle" role="group">
                  <button
                    type="button"
                    className={`compact-map-view__mode-button ${!selectionMode ? 'compact-map-view__mode-button--active' : ''}`}
                    onClick={() => setSelectionMode(false)}
                    aria-pressed={!selectionMode}
                  >
                    {t('analytics.map.mode.explore', { defaultValue: 'Explore' })}
                  </button>
                  <button
                    type="button"
                    className={`compact-map-view__mode-button ${selectionMode ? 'compact-map-view__mode-button--active' : ''}`}
                    onClick={() => setSelectionMode(true)}
                    aria-pressed={selectionMode}
                  >
                    {t('analytics.map.mode.select', { defaultValue: 'Select sections' })}
                  </button>
                </div>
                <span className="compact-map-view__mode-hint">
                  {selectionMode
                    ? t('analytics.map.hint.select', { defaultValue: 'Click areas to select' })
                    : t('analytics.map.hint.explore', { defaultValue: 'Click areas to drill' })}
                </span>
              </div>
            )}

            {showBreadcrumb && (
              <div className="compact-map-view__breadcrumb">
                {handleNavigateUp && (
                  <button
                    type="button"
                    className="compact-map-view__breadcrumb-back"
                    onClick={handleNavigateUp}
                  >
                    <ChevronLeft size={14} />
                    <span>{t('common.back', { defaultValue: 'Back' })}</span>
                  </button>
                )}
                {breadcrumbItems.length > 0 && (
                  <div className="compact-map-view__breadcrumb-trail">
                    {breadcrumbItems.map((item) => item.name).join(' / ')}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <MapContainer
          center={mapCenter}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
          zoomControl={false}
          scrollWheelZoom={false}
          zoomSnap={0.1}
          zoomDelta={0.1}
          dragging={interactive}
          touchZoom={false}
          doubleClickZoom={false}
          boxZoom={false}
          keyboard={false}
          className={`compact-map-view__map ${interactive ? '' : 'compact-map-view__map--static'}${showMarkerTooltips ? ' compact-map-view__map--markers' : ''}`}
          aria-label={ariaLabel || undefined}
          role={ariaLabel ? 'img' : undefined}
        >
        <TileLayer
          attribution='&copy; <a href="https://www.esri.com/">Esri</a> &copy; <a href="https://www.mapbox.com/">Mapbox</a>'
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          maxZoom={19}
        />

        <FitBounds stations={stations} groups={mapGroups} farm={farm} />
        <EnsureMarkerInteractivity />

        {/* Hectare squares overlay - clickable to select sections, shows selection state */}
        <AreaOverlay 
          farm={farm}
          stations={stations} 
          visible={true} 
          enableTooltips={true}
          tooltipPermanent={true}
          onAreaClick={interactive && isEditingSelection ? handleAreaClick : null}
          selectedSections={selectedSections}
          selectAllSections={selectAllSections}
          sections={sections}
          groups={mapGroups}
          groupLevel={mapGroupLevel}
        />

        {/* Visual-only station markers (non-interactive) */}
        {showMarkers && stations && stations.map((station) => {
          const icon = createCustomIcon(
            station.stationCode,
            station.status || 'active',
            station.sensorType || station.stationType
          );
          const stationLabel = station.name || station.stationCode || station.id;
          const stationType = station.sensorType || station.stationType;
          const stationTypeLabel = stationType
            ? t(`stations.types.${stationType}`, { defaultValue: stationType })
            : t('stations.types.field', { defaultValue: 'Field' });

          return (
            <Marker
              key={station.id}
              position={[station.locationLat, station.locationLon]}
              icon={icon}
              zIndexOffset={5000}
              interactive={showMarkerTooltips}
              bubblingMouseEvents={showMarkerTooltips}
            >
              {showMarkerTooltips && (
                <Tooltip direction="top" offset={[0, -12]} opacity={0.95}>
                  <div>
                    <strong>{stationLabel}</strong>
                    {stationTypeLabel ? ` • ${stationTypeLabel}` : ''}
                  </div>
                </Tooltip>
              )}
            </Marker>
          );
        })}
      </MapContainer>
      </div>

      {showLegend && (
        <div className="compact-map-view__legend">
          {showSelectionLegend && (
            <div className="compact-map-view__legend-section">
              <div className="compact-map-view__legend-markers">
                <div className="compact-map-view__legend-item">
                  <div className="compact-map-view__legend-marker compact-map-view__legend-marker--selected" />
                  <span>{t('common.selected')}</span>
                </div>
                <div className="compact-map-view__legend-item">
                  <div className="compact-map-view__legend-marker compact-map-view__legend-marker--unselected" />
                  <span>{t('common.unselected')}</span>
                </div>
              </div>
            </div>
          )}

          {showStationLegend && (
            <div className="compact-map-view__legend-section compact-map-view__legend-section--stations">
              {legendTitle && (
                <div className="compact-map-view__legend-title">{legendTitle}</div>
              )}
              <div className="compact-map-view__legend-stations">
                <div className="compact-map-view__legend-item">
                  <div
                    className="compact-map-view__legend-station-icon compact-map-view__legend-station-icon--retro"
                    aria-hidden="true"
                    dangerouslySetInnerHTML={{ __html: getStationIconSvg('retro_modulo') }}
                  />
                  <span>{t('stations.types.retro_modulo')}</span>
                </div>
                <div className="compact-map-view__legend-item">
                  <div
                    className="compact-map-view__legend-station-icon compact-map-view__legend-station-icon--campo"
                    aria-hidden="true"
                    dangerouslySetInnerHTML={{ __html: getStationIconSvg('campo_aperto') }}
                  />
                  <span>{t('stations.types.campo_aperto')}</span>
                </div>
              </div>
            </div>
          )}

          {showSelectionLegend && !isEditingSelection && (
            <div className="compact-map-view__legend-status" role="status">
              <CheckCircle size={14} className="compact-map-view__legend-status-icon" />
              <span className="compact-map-view__legend-status-text">
                {t('analytics.map.selectionSaved', {
                  defaultValue: 'Selection saved. Click Edit to change.',
                })}
              </span>
            </div>
          )}

          {showSelectionLegend && interactive && (
            <div className="compact-map-view__legend-actions">
              {isEditingSelection && hasUnsavedChanges && (
                <div className="compact-map-view__legend-buttons">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={onCancel}
                    className="compact-map-view__legend-button"
                  >
                    <X size={16} />
                    <span>{t('common.cancel')}</span>
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={onSave}
                    className="compact-map-view__legend-button"
                  >
                    <Save size={16} />
                    <span>{t('common.saveChanges')}</span>
                  </Button>
                </div>
              )}
              {!isEditingSelection && typeof onEditSelection === 'function' && (
                <div className="compact-map-view__legend-buttons">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={onEditSelection}
                    className="compact-map-view__legend-button"
                  >
                    <Edit size={16} />
                    <span>{t('analytics.map.editSelection', { defaultValue: 'Edit selection' })}</span>
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

CompactMapView.propTypes = {
  sections: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    position: PropTypes.shape({
      lat: PropTypes.number.isRequired,
      lon: PropTypes.number.isRequired,
    }).isRequired,
  })).isRequired,
  selectedSections: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectAllSections: PropTypes.bool.isRequired,
  onSectionClick: PropTypes.func,
  onSectionsChange: PropTypes.func,
  onSelectAllChange: PropTypes.func,
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  hasUnsavedChanges: PropTypes.bool,
  isEditingSelection: PropTypes.bool,
  onEditSelection: PropTypes.func,
  farm: PropTypes.object,
  stations: PropTypes.arrayOf(PropTypes.object), // Optional, for calculating positions
  showSidebar: PropTypes.bool,
  interactive: PropTypes.bool,
  showLegend: PropTypes.bool,
  legendVariant: PropTypes.oneOf(['selection', 'stations', 'both']),
  legendTitle: PropTypes.string,
  alwaysShowMarkers: PropTypes.bool,
  showMarkerTooltips: PropTypes.bool,
  ariaLabel: PropTypes.string,
  drillState: PropTypes.shape({
    level: PropTypes.oneOf(['quadrant', 'zone', 'section']),
    id: PropTypes.string,
  }),
  onDrillChange: PropTypes.func,
};

